// src/components/SelectAvailabilityFeatures.js
import React, { useEffect, useMemo, useState, useCallback, memo } from 'react';
import { toast } from 'sonner';
import { cn } from 'lib/utils';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import { Checkbox } from 'components/ui/checkbox';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import LoadingState from 'components/shared/LoadingState';
import { getAvailabilityStatsLabels, setAvailabilityStatsLabels } from '../../services/api';
import SaveBar, { useSaveShortcut, useUnsavedWarning } from './SaveBar';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const toTitleCase = (str = '') =>
  str.replace(/_/g, ' ').replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1));

// Faded tint for indeterminate (some-but-not-all) bulk checkboxes.
const TRI_STATE_CLASSES =
  'data-[state=indeterminate]:bg-primary/40 data-[state=indeterminate]:text-primary-foreground';

// ─────────────────────────────────────────────────────────────────────────────
// Memoized Row Component
// ─────────────────────────────────────────────────────────────────────────────
const AvailabilityRow = memo(({ row, idx, onUpdate }) => {
  const handleCheckedChange = useCallback(
    (checked) => {
      onUpdate(idx, 'use_in_modelling', checked ? 1 : 0);
    },
    [idx, onUpdate]
  );

  return (
    <div className="flex items-center gap-4 border-t border-borderGrey px-4 py-2 transition-colors hover:bg-neutral-50">
      {/* Field name and nicename stacked */}
      <div className="min-w-[200px] flex-1">
        <p className="text-token-base font-medium">{row.field}</p>
        <p
          className={cn(
            'text-token-sm text-muted-foreground',
            !row.stat_nicename && 'italic'
          )}
        >
          {row.stat_nicename || 'No nicename set'}
        </p>
      </div>

      {/* Use in modelling checkbox */}
      <label className="flex w-16 shrink-0 cursor-pointer items-center gap-1.5">
        <Checkbox checked={Boolean(row.use_in_modelling)} onCheckedChange={handleCheckedChange} />
        <span className="text-token-sm text-muted-foreground">MOD</span>
      </label>
    </div>
  );
});

AvailabilityRow.displayName = 'AvailabilityRow';

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
export default function SelectAvailabilityFeatures({ onDirtyChange }) {
  const [statsLabels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // ─── Fetch labels ──────────────────────────────────────────────────────────
  const fetchLabels = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const result = await getAvailabilityStatsLabels();
      setLabels(result?.statsLabels || []);
      setDirty(false);
    } catch (err) {
      console.error('Error fetching availability stats labels:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  // Report unsaved-changes state to the parent (tab-switch warning)
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useUnsavedWarning(dirty);

  // Memoized update function
  const updateRow = useCallback((rowIdx, key, value) => {
    setDirty(true);
    setLabels((prev) => {
      const newLabels = [...prev];
      newLabels[rowIdx] = { ...newLabels[rowIdx], [key]: value };
      return newLabels;
    });
  }, []);

  // Bulk enable/disable a whole accordion group
  const bulkSet = useCallback((indices, value) => {
    setDirty(true);
    setLabels((prev) => {
      const newLabels = [...prev];
      indices.forEach((i) => {
        newLabels[i] = { ...newLabels[i], use_in_modelling: value };
      });
      return newLabels;
    });
  }, []);

  // ─── Save labels ───────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      await setAvailabilityStatsLabels({ statsLabels });
      setDirty(false);
      toast.success('Availability features saved');
    } catch (err) {
      console.error('Error saving availability features', err);
      toast.error('Failed to save availability features');
    } finally {
      setSaving(false);
    }
  }, [statsLabels, saving]);

  // ─── Group rows for accordion display ─────────────────────────────────────
  const grouped = useMemo(() => {
    const groups = {};
    statsLabels.forEach((row, idx) => {
      const groupName = toTitleCase(row.category || 'Other');
      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push({ row, idx });
    });
    return groups;
  }, [statsLabels]);

  // Count of modelling-enabled features
  const modellingCount = useMemo(
    () => statsLabels.filter((row) => row.use_in_modelling).length,
    [statsLabels]
  );

  const canSave = !loading && !loadError && statsLabels.length > 0;
  useSaveShortcut(handleSubmit, canSave);

  const groupTriState = (items) => {
    const on = items.filter(({ row }) => row.use_in_modelling).length;
    if (on === 0) return false;
    return on === items.length ? true : 'indeterminate';
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="pb-24">
      {/* Header with Legend */}
      <div className="mb-4">
        <div className="mb-2 flex items-center gap-2">
          <h3 className="text-token-lg font-semibold">
            Availability Features ({modellingCount} enabled)
          </h3>
          {dirty && <Badge variant="warning">Unsaved changes</Badge>}
        </div>

        <div className="rounded-token-medium bg-muted px-4 py-3">
          <p className="text-token-base font-medium text-textSecondary">
            Select which availability features to use in the minutes prediction model
          </p>
          <p className="text-token-sm text-muted-foreground">
            MOD = Include in modelling. Nicenames are configured on the Stats Labels page.
          </p>
        </div>
      </div>

      {/* Accordions / Loader / Error / Empty */}
      {loading ? (
        <LoadingState message="Loading availability features..." />
      ) : loadError ? (
        <Alert variant="destructive">
          <AlertTitle>Failed to load availability features</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span>Something went wrong while fetching the data.</span>
            <Button variant="outline" size="sm" onClick={fetchLabels}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      ) : statsLabels.length === 0 ? (
        <p className="p-6 text-center text-token-md text-muted-foreground">
          No availability stats found.
        </p>
      ) : (
        <Accordion type="multiple" defaultValue={['10g Window']} className="space-y-2">
          {Object.entries(grouped).map(([groupName, items]) => (
            <AccordionItem
              key={groupName}
              value={groupName}
              className="overflow-hidden rounded-token-large border border-borderGrey bg-white"
            >
              <AccordionTrigger className="px-4 py-3 text-token-md font-semibold hover:no-underline">
                {groupName} ({items.length})
              </AccordionTrigger>
              <AccordionContent className="p-0">
                {/* Bulk enable/disable for the group */}
                <div className="flex items-center gap-4 border-t border-borderGrey bg-muted/50 px-4 py-1.5">
                  <span className="min-w-[200px] flex-1 text-token-sm font-medium text-muted-foreground">
                    Toggle all in group
                  </span>
                  <label className="flex w-16 shrink-0 cursor-pointer items-center gap-1.5">
                    <Checkbox
                      className={TRI_STATE_CLASSES}
                      checked={groupTriState(items)}
                      onCheckedChange={(checked) =>
                        bulkSet(items.map(({ idx }) => idx), checked ? 1 : 0)
                      }
                    />
                    <span className="text-token-sm text-muted-foreground">MOD</span>
                  </label>
                </div>
                {items.map(({ row, idx }) => (
                  <AvailabilityRow
                    key={`avail-${row.field}`}
                    row={row}
                    idx={idx}
                    onUpdate={updateRow}
                  />
                ))}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Submit Button */}
      {canSave && <SaveBar onSave={handleSubmit} saving={saving} label="Submit Features" />}
    </div>
  );
}
